import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'

import type { PartialRecord } from '@/core/helperTypes'
import type { SkippedReason, V2DatasetItemPayload } from '@/store/types'
import { V2InstanceStatus } from '@/store/types'
import { StageType, type MembershipPayload, StageActionType } from '@/store/types'
import type { V2DatasetItemTimeSummaryPayload } from '@/store/types/V2DatasetItemTimeSummaryPayload'
import type { V2WorkflowItemStatePayload } from '@/store/types/V2WorkflowItemStatePayload'
import type { V2WorkflowStageInstancePayload } from '@/store/types/V2WorkflowStageInstancePayload'
import type {
  V2ConsensusStagePayload,
  V2ParallelBlindStagePayload,
  V2WorkflowStagePayload,
} from '@/store/types/V2WorkflowStagePayload'
import { loadV2DatasetItemTimeSummary, sendV2Commands } from '@/backend/darwin'
import type { ErrorWithMessage, ParsedError, ParsedValidationError } from '@/backend/error'

import { useDatasetItemsStore } from '@/modules/Datasets/useDatasetItemsStore'
import { useDatasetStore } from '@/modules/Datasets/useDatasetStore'
import { useUserStore } from '@/modules/Auth/useUserStore'
import { useWorkviewStore } from '@/modules/Workview/useWorkviewStore'
import type { WorkflowStage } from '@/modules/Workflows/types'
import type { AxiosResponse } from 'axios'
import { getConsensusHiddenStagesIds } from '@/modules/Workflows/consensus'
import sortBy from 'lodash/sortBy'
import { getFollowingStage, getNextStage, mapStageActiontype } from './utils'
import { isUserAssignedToStageInstance } from './utils/stageAssignmentHelpers'

/**
 * Intends to hold all things related to the use of workflows within workview.
 *
 * If the item workflow gets restarted we might end having multiple stage instances for the same stage,
 * therefore it's important to differentiate between the `stageInstance?.id` and the `stage?.id` or
 * `stageInstance?.stage_id` as every stageInstance also has a `stage` property.
 *
 * Note: stage instances hold 2 repeated properties:
 * - `stage_id`: the id of the stage
 * - `stage`: the stage object
 * both referring to the static workflow stage.
 *
 * PREVIOUS/CURRENT/FOLLOWING STAGE INSTANCES
 * We need to be able to get the previous, current and following stage instances of the active item,
 * as we need to be able to render the timeline of the item and the buttons to time travel.
 * "Previous" and "Current" stage instances come from the WS message `workflow_item:state` and are stored as `itemState`.
 * "Following" stage instances are not stored in the item state, but are instead computed from the current stage instance
 * using the pure fn `getNextStage`.
 * Note: "Following" stages are stages NOT stage instances, as the item didn't traversed them yet and we lack
 * any instance.
 *
 *
 * TIME TRAVELLING
 * A user can time travel to a previous stage instance, and we need to be able to restore the state of
 * the workflow engine for that stage instance.
 * This is done using `setSelectedStage`, `setSelectedStageInstance` and `resetSelectedStage`.
 *
 * CONSENSUS/PARALLEL BLIND STAGES
 * We need to be able to check if the current stage is a consensus or parallel blind stage,
 * and if it is, we need to be able to check if the current stage instance is within a parallel blind stage,
 * as we need to handle the consensus and parallel blind stages differently within the UI/UX.
 * This is done using `isConsensus` and `isWithinParallelBlindStage`.
 */
export const useWorkflowInWorkviewStore = defineStore('workflowInWorkview', () => {
  const datasetStore = useDatasetStore()
  const datasetItemsStore = useDatasetItemsStore()
  const workviewStore = useWorkviewStore()
  const userStore = useUserStore()

  /**
   * The state of the background engine process for the active dataset item in the workflow.
   *
   * This state receives commands done in workview, processes them, and updates in memory, only
   * saving periodically.
   *
   * We receive updates for it via websockets for the currently selected item in workview.
   *
   * It contains data such as auto-completion of stage instances,
   * what the current stage instance is, dedicated assignees, etc.
   */
  const itemState = ref<V2WorkflowItemStatePayload | null>(null)

  /**
   * Map of all possible states by dataset item id
   */
  const itemStates = ref<PartialRecord<string, V2WorkflowItemStatePayload>>({})

  const selectedStageInstance = ref<V2WorkflowStageInstancePayload | null>(null)

  const selectedStage = ref<V2WorkflowStagePayload | null>(null)

  const teamSlug = computed(() => datasetStore.dataset?.team_slug)

  const selectedStageId = computed(() => selectedStage.value?.id ?? '')

  const selectedStageInstanceId = computed(() => selectedStageInstance.value?.id ?? '')

  const selectedStageType = computed((): StageType | null =>
    selectedStage.value ? selectedStage.value.type : null,
  )

  /**
   * Active workflow stages, including consensus and parallel blind internal stages.
   *
   * Note: Each stage appears only once, regardless of how many times it may be used in the workflow.
   */
  const workflowStages = computed(() => itemState.value?.workflow?.stages ?? [])

  /**
   * Current instances that the user is actively working on.
   * Current stages represent the active stages in the workflow where users can:
   * - Perform annotations if it's an annotation stage
   * - Review other's work if it's a review stage
   * - Make consensus decisions in consensus stages
   * - Process items in dataset/webhook stages
   *
   * For annotators, this only shows stages directly assigned to them.
   * For other roles (admin, reviewer etc), all current stages are shown.
   */
  const currentStageInstances = computed(() => itemState.value?.current_stage_instances || [])

  /**
   * Current stage instance for the current stage
   *
   * This comes from unparsed current stage instances, therefore for Parallel Blind Stages
   * it will return the current internal Parallel Blind Stage instance, not the parent stage instance.
   */
  const currentStageInstance = computed(() =>
    datasetItemsStore.currentItemId
      ? (itemStates.value[datasetItemsStore.currentItemId]?.current_stage_instances[0] ?? null)
      : null,
  )

  /**
   * A collection of all nested stage IDs, including consensus stages.
   * This is used to determine if a stage is nested within another stage.
   */
  const subConsensusIds = computed(() =>
    workflowStages.value.reduce<string[]>((acc, stage) => {
      if (stage.type === StageType.ConsensusEntrypoint) {
        const consensusStage = stage as V2ConsensusStagePayload
        return [...acc, ...(consensusStage.config.parallel_stage_ids ?? [])]
      }
      return acc
    }, []),
  )

  /**
   * A collection of all nested stage IDs, including parallel blind stages.
   * This is used to determine if a stage is nested within another stage.
   */
  const subParallelBlindIds = computed(() =>
    workflowStages.value.reduce<string[]>((acc, stage) => {
      if (stage.type === StageType.ParallelBlind) {
        const parallelStage = stage as V2ParallelBlindStagePayload
        return [...acc, ...(parallelStage.config?.parallel_stage_ids ?? [])]
      }
      return acc
    }, []),
  )

  /**
   * A collection of all nested stage IDs, including consensus and parallel blind stages.
   * This is used to determine if a stage is nested within another stage.
   */
  const subNestedIds = computed(() => [...subConsensusIds.value, ...subParallelBlindIds.value])

  /**
   * Parallel Blind Stage parent of the current internal parallel blind stages.
   */
  const parallelBlindStageParent = computed(() => {
    const lastInternalParallelBlindStageInstance = currentStageInstances.value.find(
      (i) => i.parent_stage_id && subParallelBlindIds.value.includes(i.stage_id),
    )

    // make sure to only return the latest instance of the parent stage, as we might have multiple instances
    // in case the workflow has been restarted
    return itemState.value?.previous_stage_instances
      .filter((i) => i.stage_id === lastInternalParallelBlindStageInstance?.parent_stage_id)
      .slice(-1)[0]
  })

  /**
   * Previous stage instances that the user has already completed/skipped.
   * These stages can be time-travelled back to by users to view past work in a readonly mode.
   * The history of all previous work and changes is preserved and can be accessed.
   *
   * Note that by manually setting a stage through the dataset-management page or by
   * restarting a workflow, these stages will be kept and shown as previous stages
   * with their complete history intact.
   */
  const previousStageInstances = computed(() => {
    const sortedPreviousStageInstances: V2WorkflowStageInstancePayload[] = sortBy(
      itemState.value?.previous_stage_instances,
      ({ data: { exited_at } }) => new Date(exited_at ?? 0).getTime(),
    )
    return (
      sortedPreviousStageInstances.reduce<V2WorkflowStageInstancePayload[]>((acc, i, idx) => {
        const isParallelBlindStage = subParallelBlindIds.value.includes(i.stage_id)
        const isLastStageInstance = idx === sortedPreviousStageInstances.length - 1

        // skip internal Parallel Blind Stages
        if (i.parent_stage_id && isParallelBlindStage) {
          return acc
        }

        // skip last Parallel Blind Stage as we want to show that as current stage
        if (isLastStageInstance && i.stage_id === parallelBlindStageParent.value?.stage_id) {
          return acc
        }

        acc.push(i)
        return acc
      }, []) ?? []
    )
  })

  /**
   * For Parallel Blind Stages, we want to show the parent stage as current
   * and hide inner stages.
   *
   * As the BE returns the parent stage as previous and its inner stages as current,
   * we need to parse the current stage instances to show the parent stage as current
   * to match designs.
   *
   * Also as we might have multiple instances of the same stage, we need to make
   * sure only the latest instance is shown as current.
   */
  const parsedCurrentStageInstances = computed(
    () =>
      currentStageInstances.value.reduce<V2WorkflowStageInstancePayload[]>((acc, i, idx) => {
        const isParallelBlindStage = subParallelBlindIds.value.includes(i.stage_id)
        const isLastStageInstance = idx === (currentStageInstances.value.length || 0) - 1

        // skip internal Parallel Blind Stages
        if (i.parent_stage_id && isParallelBlindStage) {
          // but add Parallel Blind Stage as current stage if it's the last stage instance
          if (isLastStageInstance && parallelBlindStageParent.value) {
            acc.push(parallelBlindStageParent.value)
          }

          return acc
        }

        acc.push(i)
        return acc
      }, []) ?? [],
  )

  /**
   * Assigned stage instance of the current stage, useful for annotators.
   *
   * For Parallel Blind Stages, returns null if the user has no assigned stage instance.
   * For other stages, falls back to selectedStageInstance for backward compatibility.
   * This distinction is important for PBS logic to distinguish between actual assignment vs. just having a stage selected.
   */
  const assignedStageInstance = computed(() => {
    if (!datasetItemsStore.currentItemId) {
      return null
    }

    const userId = userStore.currentUserId
    const _assignedStageInstance = userId
      ? currentStageInstances.value.find((instance) =>
          isUserAssignedToStageInstance(instance, userId),
        )
      : null

    // For Parallel Blind Stages, we need strict assignment checking
    // For other stages, fall back to selectedStageInstance for backward compatibility
    if (selectedStageType.value === StageType.ParallelBlind) {
      return _assignedStageInstance || null
    }

    return _assignedStageInstance || selectedStageInstance.value
  })

  /**
   * The annotation group id of the assigned annotate stage, or null when the
   * user isn't actively annotating in a group (review stage, etc.).
   *
   * Used to distinguish a worker's own raster from other groups' rasters so
   * consensus remap is only applied to overlays from other groups.
   */
  const assignedAnnotationGroupId = computed<string | null>(() => {
    const stage = assignedStageInstance.value?.stage
    if (stage?.type !== StageType.Annotate) {
      return null
    }
    return stage.config.annotation_group_id || null
  })

  /**
   * Annotation group ids for the current stage instances.
   *
   * If the current stage is a Parallel Blind Stage, we want to show the annotation group ids
   * for the inner stages:
   * - If the user is an annotator, we want to show all annotation group ids
   * -  Otherwise, we want to show the annotation group for the assigned user
   */
  const annotationGroupIds = computed(() => {
    if (selectedStageType.value === StageType.ParallelBlind) {
      return currentStageInstances.value.reduce<string[]>((acc, { stage, user_id }) => {
        if (stage.type !== StageType.Annotate) {
          return acc
        }

        const annotationGroupId = stage.config.annotation_group_id
        if (!annotationGroupId) {
          return acc
        }

        if (!userStore.isAnnotator) {
          acc.push(annotationGroupId)
        }

        if (user_id === userStore.currentUserId) {
          acc.push(annotationGroupId)
        }

        return acc
      }, [])
    }

    if (selectedStage.value?.type !== StageType.Annotate) {
      return []
    }

    return selectedStage.value?.config.annotation_group_id
      ? [selectedStage.value?.config.annotation_group_id]
      : []
  })

  /**
   * Following stage for the next stage in the workflow sequence.
   * Following stages represent stages that will become active after the current stages are completed.
   *
   * Special handling is done for consensus stages:
   * - If the next stage is a consensus stage, we look ahead to show the stage after consensus
   * - This helps users understand the full workflow path
   *
   * Returns null if:
   * - There are no more stages in the workflow
   * - The next stage is a consensus stage and there are no stages after it
   */
  const followingStage = computed<WorkflowStage | null>(() => {
    if (!parsedCurrentStageInstances.value) {
      return null
    }

    // We don't want to use currentStageInstances as it might have been modified
    // to show Parallel Blind Stages as current;
    // doing so would result in showing an internal Parallel Blind Stage as the following stage.
    if (!currentStageInstances.value[0]) {
      return null
    }

    const followingStage = getFollowingStage(
      workflowStages.value,
      currentStageInstances.value[0].stage_id,
    )
    if (!followingStage) {
      return null
    }

    if (followingStage.type === StageType.ConsensusEntrypoint) {
      const stageAfterConsensus = getFollowingStage(workflowStages.value, followingStage.id)
      if (!stageAfterConsensus) {
        return null
      }

      return stageAfterConsensus
    }

    return followingStage
  })

  /**
   * Checks if the current stage is a consensus stage
   * or an internal consensus stage.
   */
  const isConsensus = computed((): boolean => {
    const consensusInternalStageIds = new Set(getConsensusHiddenStagesIds(workflowStages.value))
    const isCurrentInternalStage =
      !!selectedStage.value && consensusInternalStageIds.has(selectedStageId.value)
    return (
      !!itemState.value &&
      (selectedStageType.value === StageType.ConsensusEntrypoint || isCurrentInternalStage)
    )
  })

  /**
   * A collection of all stage ids, including previous, current and following stages.
   */
  const allStageIds = computed(() => {
    const previousIds = previousStageInstances.value?.map(({ stage_id }) => stage_id) ?? []
    const currentIds = parsedCurrentStageInstances.value?.map(({ stage_id }) => stage_id) ?? []
    const followingId = followingStage.value?.id

    return [...previousIds, ...currentIds, ...(followingId ? [followingId] : [])]
  })

  /**
   * A collection of all stages instance ids, including previous and current.
   */
  const allStageInstanceIds = computed(() => {
    const previousInstanceIds = previousStageInstances.value?.map(({ id }) => id) ?? []
    const currentInstanceIds = parsedCurrentStageInstances.value?.map(({ id }) => id) ?? []

    return [...previousInstanceIds, ...currentInstanceIds]
  })

  /**
   * The key of the currently selected stage.
   * This is used to highlight the stage in the UI.
   *
   * Note that this might be a:
   * - stage instance id for previous and current stage instances
   * - stage id for following stages
   */
  const selectedStageKey = computed(() => {
    // If there's no stage instance, the use has selected a following stage
    // therefore we highlight the last stage in the workflow
    if (!selectedStageInstance.value) {
      return allStageIds.value.at(-1)
    }

    return selectedStageInstance.value.id
  })

  /**
   * Designated assignees for the selected stage.
   *
   * If the selected stage is a parallel blind stage,
   * we want to retrieve the right internal Parallel Blind Stage assignees.
   */
  const designatedAssignees = computed(() => {
    if (
      selectedStage.value?.type === StageType.ParallelBlind &&
      assignedStageInstance.value?.stage.type === StageType.Annotate &&
      assignedStageInstance.value?.stage.assignable_users?.[0]
    ) {
      return {
        [currentStageInstance.value?.parent_stage_id ?? '']:
          assignedStageInstance.value.stage.assignable_users[0].user_id,
      }
    }

    return itemState.value?.designated_assignees
  })

  const isCurrentUserADesignatedAssignee = computed(() => {
    if (!designatedAssignees.value || !userStore.currentUserId) {
      return false
    }

    return Object.values(designatedAssignees.value).includes(userStore.currentUserId)
  })

  /**
   * For the specified stage, returns the type of action which is to happen upon stage completion
   *
   * Used to decide what the continue button performs when clicking,
   * and to render what happens when autom-complete runs out.
   */
  const stageCurrentAction = computed<StageActionType | null>(() => {
    if (!selectedStage.value || !selectedStageInstance.value || !selectedStageType.value) {
      return null
    }

    const parallelStages =
      workflowStages.value
        .filter(
          (stage): stage is V2ConsensusStagePayload => stage.type === StageType.ConsensusEntrypoint,
        )
        .flatMap((cs) => [...cs.config.parallel_stage_ids]) ?? []

    const isSubAnnotate =
      selectedStageType.value === StageType.Annotate &&
      parallelStages.includes(selectedStageId.value)

    if (isSubAnnotate) {
      return StageActionType.SendToTest
    }

    if (selectedStage.value.type === StageType.ParallelBlind) {
      return StageActionType.SendToReview
    }

    if (selectedStageType.value === StageType.Annotate) {
      if (selectedStageInstance.value.data.skipped_reason) {
        return StageActionType.Skip
      }
    }

    if (selectedStageType.value === StageType.Review) {
      if (selectedStageInstance.value.data.skipped_reason) {
        return StageActionType.Archive
      }
      if (selectedStageInstance.value.data.active_edge === 'reject') {
        return StageActionType.SendBack
      }
    }

    if (selectedStageType.value === StageType.Complete) {
      return StageActionType.Completed
    }

    if (!itemState.value) {
      return null
    }

    const nextStage = getNextStage(selectedStageInstance.value, itemState.value)
    if (!nextStage) {
      return null
    }

    // Dataset can only be a starting stage, so should not happen
    if (nextStage.type === StageType.Dataset) {
      return null
    }

    return mapStageActiontype(selectedStageType.value, nextStage.type)
  })

  /**
   * Store the state of the workflow engine for a dataset item,
   * received via websockets into the store.
   *
   * For parallel blind stages, we want to show them as current rather than previous
   * to maintain visibility of all parallel work being done.
   */
  const setItemState = (state: V2WorkflowItemStatePayload | null): void => {
    itemState.value = state
  }

  /**
   * Add new item state into map of the workflow engine and set current item state.
   * Uses setItemState to ensure parallel blind stages are shown as current.
   */
  const addItemState = (itemId: string, state: V2WorkflowItemStatePayload): void => {
    setItemState(state)
    if (!itemState.value) {
      return
    }

    itemStates.value = {
      ...itemStates.value,
      [itemId]: itemState.value,
    }
  }

  /**
   * Remove item state into map of the workflow engine and set current item state to null
   */
  const removeItemState = (itemId: string): void => {
    const { [itemId]: workflowItem, ...rest } = itemStates.value

    const isRemovingCurrentItem = datasetItemsStore.currentItemId === itemId

    if (isRemovingCurrentItem) {
      selectedStage.value = null
      selectedStageInstance.value = null
    }

    itemState.value = null
    itemStates.value = { ...rest }
  }

  const setSelectedStage = (state: V2WorkflowStagePayload | null): void => {
    selectedStage.value = state
  }

  const setSelectedStageInstance = (state: V2WorkflowStageInstancePayload | null): void => {
    if (!state) {
      selectedStageInstance.value = null
      return
    }

    if (state.stage.type !== StageType.ParallelBlind) {
      selectedStageInstance.value = state
      return
    }

    const selectedUserId =
      currentStageInstances.value.find((i) => i.user_id === userStore.currentUserId)?.user_id ||
      currentStageInstances.value[0]?.user_id

    selectedStageInstance.value = {
      ...state,
      status: V2InstanceStatus.Current,
      user_id: selectedUserId,
    }
  }

  /**
   * Resets the selected stage instance and stage
   */
  const resetSelectedStage = (): void => {
    if (!itemState.value) {
      return
    }

    // if the user is an annotator, select the latest stage instance assigned to them
    // otherwise select the latest stage instance if the user is an admin/reviewer
    const currentStageInstance = parsedCurrentStageInstances.value.find((i, idx) => {
      const isLatest = idx === parsedCurrentStageInstances.value.length - 1
      return (
        (!userStore.isAnnotator && isLatest) ||
        i.user_id === userStore.currentUserId ||
        i.stage.type === StageType.ParallelBlind
      )
    })

    const firstStageInstance = previousStageInstances.value[0]
    const instance = currentStageInstance || firstStageInstance || null

    setSelectedStage(instance.stage)
    setSelectedStageInstance(instance)
  }

  /**
   * Store the time summaries for a dataset item.
   *
   * Used to store the time summaries for a dataset item.
   */
  const itemTimeSummaries = reactive<PartialRecord<string, V2DatasetItemTimeSummaryPayload>>({})
  const setItemTimeSummaries = (state: V2DatasetItemTimeSummaryPayload): void => {
    itemTimeSummaries[state.dataset_item_id] = state
  }

  /**
   * Assigns a stage to a member, when clicking the continue button.
   */
  const assignStage = async ({
    stage,
    member,
  }: {
    stage: V2WorkflowStagePayload
    member: MembershipPayload
  }): Promise<{ error?: ErrorWithMessage | ParsedValidationError; data?: undefined }> => {
    if (!datasetItemsStore.currentItemId) {
      throw new Error('Item not set')
    }

    if (!datasetStore.dataset) {
      throw new Error('Dataset not set')
    }

    if (!itemState.value) {
      throw new Error('Item state not loaded')
    }

    const params: Parameters<typeof sendV2Commands>[0] = {
      datasetItemId: datasetItemsStore.currentItemId,
      teamSlug: datasetStore.dataset.team_slug,
      commands: [
        {
          type: 'assign',
          data: {
            assignee_id: member.user_id,
            stage_id: stage.id,
          },
        },
      ],
    }
    const response = await sendV2Commands(params)

    if ('error' in response) {
      workviewStore.setError(response.error)
      return { error: response.error }
    }

    return { data: undefined }
  }

  /**
   * Used to restart an item when clicking the continue button.
   */
  const restartItem = async (): Promise<{
    error?: ErrorWithMessage | ParsedValidationError
    data?: undefined
  }> => {
    if (!datasetItemsStore.currentItemId) {
      throw new Error('Item not set')
    }

    if (!itemState.value) {
      throw new Error('Item state not loaded')
    }

    const stageInstance = currentStageInstances.value.find(
      (i) => i.stage.type === StageType.Complete,
    )

    if (!stageInstance) {
      throw new Error('Current stage is of wrong type')
    }

    if (!datasetStore.dataset) {
      throw new Error('Dataset not set')
    }

    if (!teamSlug.value) {
      throw new Error('Team slug not set')
    }

    const stage = workflowStages.value.find((s) => 'initial' in s.config && s.config.initial)

    if (!stage) {
      throw new Error('Workflow has no initial stage')
    }

    const params: Parameters<typeof sendV2Commands>[0] = {
      datasetItemId: datasetItemsStore.currentItemId,
      teamSlug: teamSlug.value,
      commands: [
        {
          type: 'set_stage',
          data: {
            to_stage_id: stage.id,
          },
        },
      ],
    }

    const response = await sendV2Commands(params)

    if ('error' in response) {
      workviewStore.setError(response.error)
      return { error: response.error }
    }

    return { data: undefined }
  }

  /**
   * Loads the time summary for a dataset item.
   */
  const loadTimeSummary = async ({
    teamSlug,
    item,
  }: {
    teamSlug: string
    item: V2DatasetItemPayload
  }): Promise<AxiosResponse<V2DatasetItemTimeSummaryPayload, unknown> | ParsedError> => {
    const response = await loadV2DatasetItemTimeSummary({ datasetItemId: item.id, teamSlug })

    if ('data' in response) {
      setItemTimeSummaries(response.data)
    }

    return response
  }

  /**
   * Skips the selected stage instance.
   */
  const skipSelectedStageInstance = async (
    reason: SkippedReason,
  ): Promise<{
    data?: SkippedReason
    error?: ErrorWithMessage | ParsedValidationError
  }> => {
    const itemsStore = useDatasetItemsStore()
    if (!itemsStore.currentItemId) {
      throw new Error('Item not set')
    }

    if (!datasetStore.dataset) {
      throw new Error('Dataset not set')
    }

    if (!teamSlug.value) {
      throw new Error('Team slug not set')
    }

    if (!selectedStageInstance.value) {
      throw new Error(
        'useWorkflowInWorkviewStore, skipSelectedStageInstance, No stage instance selected',
      )
    }

    const commands: Parameters<typeof sendV2Commands>[0]['commands'] = []

    if (!selectedStageInstance.value.user_id && userStore.currentUser) {
      // we want to assign the stage to the current user if not already assigned
      commands.push({
        type: 'assign',
        data: {
          assignee_id: userStore.currentUserId,
          stage_id: selectedStageInstance.value.stage_id,
        },
      })
    }

    // the primary command we always send is skip
    commands.push({
      type: 'skip',
      data: { reason, stage_id: selectedStageInstance.value.stage_id },
    })

    const response = await sendV2Commands({
      datasetItemId: itemsStore.currentItemId,
      teamSlug: teamSlug.value,
      commands,
    })

    if ('error' in response) {
      workviewStore.setError(response.error)
      return { error: response.error }
    }

    return { data: undefined }
  }

  /**
   * Cleans up the store.
   */
  const cleanup = (): void => {
    itemState.value = null
    itemStates.value = {}
    selectedStage.value = null
    selectedStageInstance.value = null
    Object.assign(itemTimeSummaries, {})
  }

  return {
    selectedStage,
    selectedStageId,
    selectedStageType,
    selectedStageInstance,
    selectedStageInstanceId,
    workflowStages,
    previousStageInstances,
    currentStageInstances,
    parsedCurrentStageInstances,
    currentStageInstance,
    parallelBlindStageParent,
    assignedStageInstance,
    assignedAnnotationGroupId,
    designatedAssignees,
    isCurrentUserADesignatedAssignee,
    followingStage,
    itemState,
    itemStates,
    isConsensus,
    itemTimeSummaries,
    stageCurrentAction,
    subNestedIds,
    allStageIds,
    allStageInstanceIds,
    selectedStageKey,
    annotationGroupIds,
    setSelectedStage,
    setSelectedStageInstance,
    resetSelectedStage,
    setItemState,
    addItemState,
    removeItemState,
    setItemTimeSummaries,
    assignStage,
    restartItem,
    loadTimeSummary,
    skipSelectedStageInstance,
    cleanup,
  }
})
